<template>
  <va-form
    :id="alternativeContent ? alternativeContent.id : ''"
    class="create-alternative-content__form"
    ref="form"
    :item="alternativeContent"
    v-model="form"
    :modify-before-send="prepareFormBeforeSave"
    :required-fields-set="requiredFieldsSet"
    @finishedSaving="finishedSaving">
    <Modal
      width="400"
      :value="displaySaveWarningModal"
      :title="$t('resources.alternative_contents.warning_modal_title')"
      :subtitle="$t('resources.alternative_contents.warning_modal_subtitle')"
      :cta-text="$t('generic.buttons.save')"
      cta-color="primary"
      dialog-icon="info"
      @confirm="confirmSave"
      @cancel="cancelSave">
    </Modal>
    <!-- Details -->
    <v-card class="spacing-bottom--24">
      <v-card-title>{{ $t("resources.alternative_contents.details_block_title") }}</v-card-title>
      <v-card-subtitle class="spacing-bottom--16">{{
        $t("resources.alternative_contents.details_block_subtitle")
      }}</v-card-subtitle>
      <div v-if="form.updatedAt" class="spacing-bottom--16">
        <div class="updated-at-label">
          {{ $t("resources.headers.fields.last_updated") }}
        </div>
        <div class="updated-at-time">
          {{ getFullDateAndTime(form.updatedAt) }}
          {{ form.updatedBy && `by ${form.updatedBy.firstName} ${form.updatedBy.lastName}` }}
        </div>
      </div>
      <va-text-input
        v-model="form.name"
        source="name"
        required
        :rules="[
          v => !!v || this.$t('va.forms.required_field', { field: $t('resources.alternative_contents.fields.name') }),
        ]"
        class="spacing-bottom--16"
        :label="$t('resources.alternative_contents.fields.name')"
        :placeholder="$t('resources.alternative_contents.placeholders.name')"></va-text-input>
      <div class="spacing-top--16">
        <div class="d-flex spacing-bottom--16">
          <va-autocomplete-input
            class="mr-2 prepend-icon-hidden"
            :label="$t('resources.pages.page_alternative_content_countries_label')"
            :value="form.conditionals.selectedCountries"
            :choices="countriesData"
            :source="'form.conditionals.selectedCountries'"
            cache-items
            :placeholder="$t('resources.pages.page_alternative_content_countries_placeholder')"
            @change="data => updateAlternativeContentConditions('selectedCountries', data)"
            multiple></va-autocomplete-input>

          <va-autocomplete-input
            class="ml-2 prepend-icon-hidden"
            :label="$t('resources.pages.page_alternative_content_states_label')"
            :value="form.conditionals.selectedStates"
            :choices="statesData"
            :source="'form.conditionals.selectedStates'"
            cache-items
            @change="data => updateAlternativeContentConditions('selectedStates', data)"
            :placeholder="$t('resources.pages.page_alternative_content_states_placeholder')"
            multiple></va-autocomplete-input>
        </div>
        <div class="d-flex spacing-bottom--16">
          <va-autocomplete-input
            class="mr-2 prepend-icon-hidden"
            :label="$t('resources.pages.page_alternative_content_cities_label')"
            :value="form.conditionals.selectedCities"
            :choices="citiesData"
            :source="'form.conditionals.selectedCities'"
            cache-items
            @change="data => updateAlternativeContentConditions('selectedCities', data)"
            :placeholder="$t('resources.pages.page_alternative_content_cities_placeholder')"
            multiple
            hideSelectAll></va-autocomplete-input>

          <va-autocomplete-input
            class="ml-2 prepend-icon-hidden"
            :label="$t('resources.pages.page_alternative_content_counties_label')"
            :value="form.conditionals.selectedCounties"
            :choices="countiesData"
            :source="'form.conditionals.selectedCounties'"
            cache-items
            @change="data => updateAlternativeContentConditions('selectedCounties', data)"
            :placeholder="$t('resources.pages.page_alternative_content_counties_placeholder')"
            multiple></va-autocomplete-input>
        </div>
        <div class="d-flex spacing-bottom--16">
          <va-select-input
            :label="$t('resources.pages.page_alternative_content_devices_label')"
            class="device__select prepend-icon-hidden"
            multiple
            :value="form.conditionals.selectedDevices"
            :source="'form.conditionals.selectedDevices'"
            :choices="devices"
            cache-items
            @change="data => updateAlternativeContentConditions('selectedDevices', data)"
            :placeholder="$t('resources.pages.page_alternative_content_devices_placeholder')"
            :menu-props="{ offsetY: true, offsetOverflow: true }"></va-select-input>
        </div>
      </div>
      <div class="d-flex py-3">
        <v-switch
          class="enable-utm-parameters-switch"
          :label="$t('resources.pages.page_alternative_content_utm_parameters_label')"
          v-model="form.conditionals.enableUTMParameters"
          @change="enableUTMParametersChange"
          color="primary"
          :ripple="false"
          hide-details
          inset></v-switch>
        <info-tooltip
          class="enable-utm-parameters-switch-tooltip"
          :info="$t('resources.pages.page_alternative_content_utm_parameters_tooltip')"></info-tooltip>
      </div>

      <!-- UTM params -->
      <v-expand-transition>
        <div v-if="form.conditionals.enableUTMParameters" class="expandable-content">
          <div class="d-flex spacing-bottom--16 spacing-top--16">
            <va-text-input
              class="domain-details__card--url flex-1 mr-2"
              v-model.trim="form.conditionals.utmSource"
              :prefix="'UTM_SOURCE='"
              :label="'Source'"
              :placeholder="'SOURCE'"></va-text-input>
            <va-text-input
              class="domain-details__card--url flex-1 ml-2"
              v-model.trim="form.conditionals.utmMedium"
              :prefix="'UTM_MEDIUM='"
              :label="'Medium'"
              :placeholder="'MEDIUM'"></va-text-input>
          </div>

          <div class="d-flex spacing-bottom--16">
            <va-text-input
              class="domain-details__card--url flex-1 mr-2"
              v-model.trim="form.conditionals.utmCampaign"
              :prefix="'UTM_CAMPAIGN='"
              :label="'Campaign'"
              :placeholder="'CAMPAIGN'"></va-text-input>
            <va-text-input
              class="domain-details__card--url flex-1 ml-2"
              v-model.trim="form.conditionals.utmTerm"
              :prefix="'UTM_TERM='"
              :label="'Term'"
              :placeholder="'TERM'"></va-text-input>
          </div>

          <div class="d-flex">
            <va-text-input
              class="domain-details__card--url pr-2 max-w-50"
              v-model.trim="form.conditionals.utmContent"
              :prefix="'UTM_CONTENT='"
              :label="'Content'"
              :placeholder="'CONTENT'"></va-text-input>
          </div>
        </div>
      </v-expand-transition>

      <v-text-field
        class="spacing-bottom--16 spacing-top--16"
        v-model="form.headline"
        required
        persistent-placeholder
        hide-details
        :label="$t('resources.pages.page_alternative_content_headline')"
        :placeholder="$t('resources.pages.headline_placeholder')"></v-text-field>

      <va-tinymce-input
        class="spacing-bottom--16 spacing-top--16"
        :title="$t('resources.pages.page_alternative_content')"
        :current-content="form.content"
        :editor-height="739"
        :imageUploadQueue.sync="alternativeContentImageUploadQueue"
        @update="updateAlternativeContent">
        <template #info>
          <div class="tinymce-info-wrapper">
            <v-icon color="info" size="24">mdi-information</v-icon>
            <ul>
              <li>{{ $t("resources.alternative_contents.info_1") }}</li>
              <li v-if="!urlExistsInContent">{{ $t("resources.alternative_contents.info_2") }}</li>
            </ul>
          </div>
        </template>
      </va-tinymce-input>
      <VaJsonInput
        :label="$t('resources.pages.page_alternative_content_json_label')"
        v-model="alternativeContentConditionsJson"></VaJsonInput>
    </v-card>
  </va-form>
</template>

<script>
  import crudFormMixin from "@/mixins/crudFormMixin";
  import statesData from "@/assets/data/states";
  import citiesData from "@/assets/data/cities";
  import countiesData from "@/assets/data/counties";
  import countriesData from "@/assets/data/countries";
  import devicesData from "@/assets/data/devices";
  import InfoTooltip from "@/components/custom/InfoTooltip";
  import VaJsonInput from "@/components/custom/VaJsonInput";
  import { parseToHtml } from "@/utility/parseToHtml";
  import { convertAssetsPathToAbsoluteLinks, convertAssetsPathToRelativeLinks } from "@/utility/convertAssetsPaths";
  import { cleanupTinymceContent } from "@/utility/cleanupTinymceContent";
  import { serializeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
  import { getFullDateAndTime } from "@core/utils/filter";
  import Modal from "@/components/custom/Modal";

  export default {
    name: "AuthorForm",
    components: { InfoTooltip, VaJsonInput, Modal },
    mixins: [crudFormMixin],
    props: {
      alternativeContent: {
        type: Object,
        default: () => {},
      },
    },
    data() {
      return {
        requiredFormFields: ["name"],
        form: {},
        statesData,
        citiesData,
        countiesData,
        countriesData,
        devices: devicesData,
        alternativeContentImageUploadQueue: [],
        getFullDateAndTime,
        displaySaveWarningModal: false,
        urlExistsInContent: false,
        defaultAlternativeContentConditions: {
          enableUTMParameters: false,
          utmSource: "",
          utmMedium: "",
          utmCampaign: "",
          utmTerm: "",
          utmContent: "",
          selectedStates: [],
          selectedCities: [],
          selectedCounties: [],
          selectedCountries: [],
          selectedDevices: [],
        },
      };
    },
    beforeMount() {
      this.form = this.alternativeContent ? this.alternativeContent : {};
      this.form.conditionals = this.alternativeContent?.conditionals || {
        ...this.defaultAlternativeContentConditions,
      };
      this.updateAlternativeContent(convertAssetsPathToAbsoluteLinks(this.alternativeContent?.content));
    },
    computed: {
      alternativeContentConditionsJson: {
        get() {
          if (this.form.conditionals.enableUTMParameters) {
            return this.form.conditionals;
          } else {
            return {
              selectedStates: this.form.conditionals.selectedStates,
              selectedCities: this.form.conditionals.selectedCities,
              selectedCounties: this.form.conditionals.selectedCounties,
              selectedCountries: this.form.conditionals.selectedCountries,
              selectedDevices: this.form.conditionals.selectedDevices,
            };
          }
        },
        set(value) {
          if (this.form.conditionals.enableUTMParameters) {
            this.form.conditionals.utmSource = value.utmSource;
            this.form.conditionals.utmMedium = value.utmMedium;
            this.form.conditionals.utmCampaign = value.utmCampaign;
            this.form.conditionals.utmTerm = value.utmTerm;
            this.form.conditionals.utmContent = value.utmContent;
          }
          this.form.conditionals.selectedStates = value.selectedStates || [];
          this.form.conditionals.selectedCities = value.selectedCities || [];
          this.form.conditionals.selectedCounties = value.selectedCounties || [];
          this.form.conditionals.selectedCountries = value.selectedCountries || [];
          this.form.conditionals.selectedDevices = value.selectedDevices || [];
        },
      },
    },
    methods: {
      updateAlternativeContentConditions(field, data) {
        this.form.conditionals[field] = data;
      },
      prepareFormBeforeSave(data) {
        data.content = {};

        if (this.form.content) {
          const htmlElementFromContent = convertAssetsPathToRelativeLinks(this.form.content);

          cleanupTinymceContent(htmlElementFromContent);

          serializeDynamicComponents(htmlElementFromContent);
          data.content = htmlElementFromContent.body.innerHTML;
        } else {
          data.content = "";
        }
        data.headline = this.form?.headline || "";

        return data;
      },
      finishedSaving() {
        this.$emit("finishedSaving");
      },
      updateAlternativeContent(newValue) {
        let htmlElementFromContent = parseToHtml(newValue);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        this.form.content = htmlElementFromContent.body.innerHTML;

        if (this.form.content?.includes("%URL%")) {
          this.urlExistsInContent = true;
        } else {
          this.urlExistsInContent = false;
        }
      },
      enableUTMParametersChange(val) {
        if (!val) {
          this.form.conditionals.utmSource = "";
          this.form.conditionals.utmMedium = "";
          this.form.conditionals.utmCampaign = "";
          this.form.conditionals.utmTerm = "";
          this.form.conditionals.utmContent = "";
        }
      },
      submit() {
        this.setSubmitAttempted();

        if (this.alternativeContentImageUploadQueue?.length > 0) {
          this.$admin.toast.error(this.$t("resources.pages.images_still_uploading"));
          this.finishedSaving();

          return;
        }

        if (!this.form.id) {
          this.$refs.form.onSubmit();

          return;
        }

        if (this.$refs.form.$refs.form.validate()) {
          this.displaySaveWarningModal = true;
        }
      },
      confirmSave() {
        this.displaySaveWarningModal = false;
        this.$refs.form.onSubmit();
      },
      cancelSave() {
        this.finishedSaving();
        this.displaySaveWarningModal = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .prepend-icon-hidden {
    &::v-deep {
      .v-input__prepend-inner {
        display: none;
      }
      .v-input.v-select.v-select--is-multi.v-autocomplete--with-label .v-label,
      .v-input.v-autocomplete.v-select--is-multi.v-autocomplete--with-label .v-label {
        left: 0 !important;
      }
      .v-input.v-select.v-select--is-multi .v-label {
        left: 0 !important;
      }
    }
  }

  .enable-utm-parameters-switch {
    margin-right: 2px;
    &::v-deep {
      .v-input--selection-controls__input {
        margin-right: 6px;
      }
    }
  }

  .updated-at-label {
    @include text-caption-bold();
    color: get-color("subtitle");
  }

  .updated-at-time {
    @include text-body-2();
    color: get-color("subtitle");
  }

  .tinymce-info-wrapper {
    padding: 12px 16px;
    border: 1px solid get-color("accent");
    background: get-color("accent-120");
    border-radius: 4px;
    margin-bottom: 6px;
    display: flex;
    gap: 12px;
    align-items: center;

    ul {
      @include text-caption();
      color: get-color("menu");

      li:not(:last-child) {
        margin-bottom: 4px;
      }
    }
  }
</style>
